import {
    PageBuilderWidgetModel,
    PageBuilderImagePropertiesModel,
    PageBuilderSpacingModel,
} from "@de-trustleap/shared-models";
import { getAlignmentStyles } from "../utils/alignmentUtils";
import { BaseWidget } from "./baseWidget";
import { getRecommendationData } from "../context/recommendationContext";

export class ImageWidget extends BaseWidget<PageBuilderImagePropertiesModel> {
    private padding: PageBuilderSpacingModel;
    private margin: PageBuilderSpacingModel;
    private alignment: string;

    constructor(widget: PageBuilderWidgetModel) {
        super(widget.properties as PageBuilderImagePropertiesModel);
        this.padding = new PageBuilderSpacingModel(widget.padding || {});
        this.margin = new PageBuilderSpacingModel(widget.margin || {});
        this.alignment = widget.alignment || "center";
    }

    async render(): Promise<HTMLElement> {
        const wrapper = this.createImageWrapper();
        const imageContainer = this.createImageContainer();

        const image = this.createImageElement();
        image.onerror = () => this.handleImageError(imageContainer, image);

        imageContainer.appendChild(image);

        if (this.properties.overlayColor) {
            const overlay = this.createOverlay();
            imageContainer.appendChild(overlay);
        }

        wrapper.appendChild(imageContainer);
        return wrapper;
    }

    private createImageWrapper(): HTMLElement {
        const { justifyContent, alignItems } = getAlignmentStyles(this.alignment);

        const wrapper = document.createElement("div");
        wrapper.classList.add("image-widget");

        this.applyStyles(wrapper, {
            display: "flex",
            boxSizing: "border-box",
            justifyContent,
            alignItems,
        });

        this.applySpacing(wrapper, this.padding.toMap(), this.margin.toMap());
        return wrapper;
    }

    private createImageContainer(): HTMLElement {
        const container = document.createElement("div");

        this.applyStyles(container, {
            position: "relative",
            width: `${this.properties.width || 200}px`,
            height: `${this.properties.height || 184}px`,
            overflow: "hidden",
        });

        return container;
    }

    private createImageElement(): HTMLImageElement {
        const img = document.createElement("img");
        img.src = this.getImageSource();
        img.loading = "lazy";

        this.applyStyles(img, {
            width: "100%",
            height: "100%",
            borderRadius: `${this.properties.borderRadius || 0}px`,
            objectFit: this.properties.contentMode || "cover",
            objectPosition: "center",
        });

        return img;
    }

    private getImageSource(): string {
        const recommendation = getRecommendationData();
        const promoterUrl = recommendation?.promoterImageDownloadURL?.trim();

        if (promoterUrl) return promoterUrl;
        if (this.properties.newImageBase64) return this.properties.newImageBase64;
        if (this.properties.url) return this.properties.url;

        return "";
    }

    private createOverlay(): HTMLElement {
        const overlay = document.createElement("div");
        this.applyStyles(overlay, {
            position: "absolute",
            top: "0",
            left: "0",
            width: "100%",
            height: "100%",
            backgroundColor: this.formatColor(this.properties.overlayColor),
            pointerEvents: "none",
            borderRadius: `${this.properties.borderRadius || 0}px`,
        });
        return overlay;
    }

    private handleImageError(container: HTMLElement, image: HTMLImageElement): void {
        image.style.display = "none";

        const fallback = document.createElement("div");
        this.applyStyles(fallback, {
            background: "#ccc",
            borderRadius: `${this.properties.borderRadius || 10}px`,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
        });

        fallback.innerHTML = `<span style="color:#ffffff; font-size:18px; font-family:Arial, sans-serif;">Image Not Found</span>`;
        container.appendChild(fallback);
    }
}
